import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useSearchParams } from 'react-router-dom';
import * as XLSX from 'xlsx';
import {
  fetchCutLists,
  createCutList,
  generateCutLists,
  previewCutLists,
  clearPreview,
  updateCutListStatus,
  deleteCutList,
  deleteAllCutLists
} from '../store/slices/cutlistsSlice';
import { fetchMachines } from '../store/slices/machinesSlice';

// Priority from which an order counts as urgent (marketplace orders get 8)
const URGENT_PRIORITY = 8;

const StatusBadge = ({ status }) => {
  const colors = {
    draft: 'secondary',
    ready: 'info',
    in_progress: 'warning',
    completed: 'success',
    cancelled: 'dark'
  };

  const labels = {
    draft: 'Concept',
    ready: 'Gereed',
    in_progress: 'In productie',
    completed: 'Voltooid',
    cancelled: 'Geannuleerd'
  };

  return (
    <span className={`badge bg-${colors[status] || 'secondary'}`}>
      {labels[status] || status}
    </span>
  );
};

const CutLists = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { cutLists, pagination, loading, generating, previewing, preview } = useSelector((state) => state.cutlists);
  const { machines } = useSelector((state) => state.machines);

  // Restore filters from URL params first, then sessionStorage, then defaults
  const getInitialFilters = () => {
    // URL params take priority (e.g. from browser-back or direct link)
    if (searchParams.toString()) {
      return {
        machine: searchParams.get('machine') || '',
        status: searchParams.get('status') || 'pending',
        search: searchParams.get('search') || '',
        page: parseInt(searchParams.get('page'), 10) || 1
      };
    }
    // Fall back to sessionStorage (persists across sidebar navigation)
    try {
      const saved = sessionStorage.getItem('cutlistsFilters');
      if (saved) return JSON.parse(saved);
    } catch (e) { /* ignore */ }
    // Defaults
    return { machine: '', status: 'pending', search: '', page: 1 };
  };

  const [filters, setFiltersState] = useState(getInitialFilters);

  const setFilters = useCallback((newFilters) => {
    setFiltersState(newFilters);
    // Persist to sessionStorage
    sessionStorage.setItem('cutlistsFilters', JSON.stringify(newFilters));
    // Sync to URL
    const params = new URLSearchParams();
    if (newFilters.machine) params.set('machine', newFilters.machine);
    if (newFilters.status && newFilters.status !== 'pending') params.set('status', newFilters.status);
    if (newFilters.search) params.set('search', newFilters.search);
    if (newFilters.page > 1) params.set('page', newFilters.page.toString());
    setSearchParams(params, { replace: true });
  }, [setSearchParams]);

  // On mount: sync initial filters to URL
  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.machine) params.set('machine', filters.machine);
    if (filters.status && filters.status !== 'pending') params.set('status', filters.status);
    if (filters.search) params.set('search', filters.search);
    if (filters.page > 1) params.set('page', filters.page.toString());
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [searchInput, setSearchInput] = useState(filters.search);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [expandedDeviations, setExpandedDeviations] = useState({});
  const [itemOverrides, setItemOverrides] = useState({}); // { orderId_itemId: { machineId, accepted } }
  const [productMachineOverrides, setProductMachineOverrides] = useState({}); // { productCode: machineId }
  const [newCutList, setNewCutList] = useState({
    machineId: '',
    material: '',
    name: ''
  });
  const [generateOptions, setGenerateOptions] = useState({
    machineIds: []
  });
  const [openDropdown, setOpenDropdown] = useState(null); // cutList._id of open dropdown
  const [selectedCutLists, setSelectedCutLists] = useState(new Set());

  useEffect(() => {
    dispatch(fetchCutLists(filters));
    dispatch(fetchMachines());
  }, [dispatch, filters]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.dropdown')) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleCreate = () => {
    dispatch(createCutList(newCutList));
    setShowCreateModal(false);
    setNewCutList({ machineId: '', material: '', name: '' });
  };

  const handleGenerate = () => {
    // Convert itemOverrides to array format for backend
    const overrides = Object.entries(itemOverrides)
      .filter(([_, value]) => value.machineId) // Only include items with machine override
      .map(([key, value]) => {
        const [orderId, itemId] = key.split('_');
        return {
          orderId,
          itemId,
          machineId: value.machineId
        };
      });

    // Only send productOverrides for products the user manually changed.
    // Non-overridden products will be auto-routed by generate (same logic as preview),
    // which correctly handles priority-shape splitting for duplicate productCodes.
    const productOverrides = [];
    for (const [productCode, machineId] of Object.entries(productMachineOverrides)) {
      if (machineId) {
        productOverrides.push({ productCode, machineId });
      }
    }

    dispatch(generateCutLists({ ...generateOptions, overrides, productOverrides })).then(() => {
      dispatch(fetchCutLists(filters));
      setShowPreviewModal(false);
      dispatch(clearPreview());
      setItemOverrides({});
      setProductMachineOverrides({});
    });
  };

  const handleClosePreview = () => {
    setShowPreviewModal(false);
    dispatch(clearPreview());
    setExpandedDeviations({});
    setItemOverrides({});
    setProductMachineOverrides({});
  };

  // Export cutlists to Excel
  const handleExportExcel = () => {
    const wb = XLSX.utils.book_new();

    // Sheet 1: Overview (Overzicht)
    const overviewData = [
      ['Snijlijsten Export', '', '', '', ''],
      ['Geëxporteerd op:', new Date().toLocaleString('nl-NL'), '', '', ''],
      ['', '', '', '', ''],
      ['Samenvatting', '', '', '', ''],
      ['Totaal snijlijsten:', cutLists.length, '', '', ''],
      ['', '', '', '', ''],
      ['Status Overzicht:', '', '', '', ''],
      ['Concept:', cutLists.filter(cl => cl.status === 'draft').length, '', '', ''],
      ['Gereed:', cutLists.filter(cl => cl.status === 'ready').length, '', '', ''],
      ['In productie:', cutLists.filter(cl => cl.status === 'in_progress').length, '', '', ''],
      ['Voltooid:', cutLists.filter(cl => cl.status === 'completed').length, '', '', ''],
    ];
    const wsOverview = XLSX.utils.aoa_to_sheet(overviewData);
    wsOverview['!cols'] = [{ wch: 20 }, { wch: 25 }, { wch: 15 }, { wch: 15 }, { wch: 15 }];
    XLSX.utils.book_append_sheet(wb, wsOverview, 'Overzicht');

    // Sheet 2: All Cutlists (Alle Snijlijsten)
    const cutListsHeader = [
      'Snijlijst ID', 'Naam', 'Machine', 'Status', 'Aantal Items',
      'Totaal Stuks', 'Voltooid', 'Aangemaakt'
    ];
    const cutListsRows = cutLists.map(cl => [
      cl.cutListId || cl._id,
      cl.name || '-',
      cl.machine?.name || '-',
      cl.status === 'draft' ? 'Concept' :
        cl.status === 'ready' ? 'Gereed' :
        cl.status === 'in_progress' ? 'In productie' :
        cl.status === 'completed' ? 'Voltooid' : cl.status,
      cl.items?.length || 0,
      cl.items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0,
      cl.stats?.completedItems || 0,
      new Date(cl.createdAt).toLocaleString('nl-NL')
    ]);
    const wsCutLists = XLSX.utils.aoa_to_sheet([cutListsHeader, ...cutListsRows]);
    wsCutLists['!cols'] = [
      { wch: 15 }, { wch: 30 }, { wch: 20 }, { wch: 15 },
      { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 20 }
    ];
    XLSX.utils.book_append_sheet(wb, wsCutLists, 'Alle Snijlijsten');

    // Sheet 3: All Items (Alle Items) - detailed
    const itemsHeader = [
      'Snijlijst', 'Machine', 'Order Nr', 'Product Code', 'Product Naam',
      'Aantal', 'Breedte (cm)', 'Lengte (cm)', 'Vorm', 'Materiaal',
      'Prioriteit', 'Status'
    ];
    const itemsRows = [];
    cutLists.forEach(cl => {
      (cl.items || []).forEach(item => {
        itemsRows.push([
          cl.name || cl.cutListId || cl._id,
          cl.machine?.name || '-',
          item.orderNumber || '-',
          item.productCode || '-',
          item.productName || '-',
          item.quantity || 0,
          item.dimensions?.width || '-',
          item.dimensions?.height || '-',
          item.shape || '-',
          item.material || '-',
          item.priority || 0,
          item.status === 'pending' ? 'In wachtrij' :
            item.status === 'completed' ? 'Voltooid' :
            item.status === 'failed' ? 'Mislukt' : item.status || '-'
        ]);
      });
    });
    const wsItems = XLSX.utils.aoa_to_sheet([itemsHeader, ...itemsRows]);
    wsItems['!cols'] = [
      { wch: 25 }, { wch: 20 }, { wch: 15 }, { wch: 20 }, { wch: 35 },
      { wch: 10 }, { wch: 12 }, { wch: 12 }, { wch: 15 }, { wch: 15 },
      { wch: 10 }, { wch: 12 }
    ];
    XLSX.utils.book_append_sheet(wb, wsItems, 'Alle Items');

    // Sheet 4: Per Machine
    const machineGroups = {};
    cutLists.forEach(cl => {
      const machineName = cl.machine?.name || 'Niet toegewezen';
      if (!machineGroups[machineName]) {
        machineGroups[machineName] = { cutLists: [], totalItems: 0, totalQuantity: 0 };
      }
      machineGroups[machineName].cutLists.push(cl);
      machineGroups[machineName].totalItems += cl.items?.length || 0;
      machineGroups[machineName].totalQuantity += cl.items?.reduce((sum, i) => sum + (i.quantity || 0), 0) || 0;
    });

    const machineHeader = ['Machine', 'Aantal Snijlijsten', 'Totaal Items', 'Totaal Stuks'];
    const machineRows = Object.entries(machineGroups).map(([name, data]) => [
      name,
      data.cutLists.length,
      data.totalItems,
      data.totalQuantity
    ]);
    const wsMachines = XLSX.utils.aoa_to_sheet([machineHeader, ...machineRows]);
    wsMachines['!cols'] = [{ wch: 25 }, { wch: 18 }, { wch: 15 }, { wch: 15 }];
    XLSX.utils.book_append_sheet(wb, wsMachines, 'Per Machine');

    // Generate filename with date
    const dateStr = new Date().toISOString().split('T')[0];
    const filename = `snijlijsten_export_${dateStr}.xlsx`;

    // Save file
    XLSX.writeFile(wb, filename);
  };

  // Export preview to Excel (when preview modal is open)
  const handleExportPreviewExcel = () => {
    if (!preview.products?.length) return;

    const wb = XLSX.utils.book_new();

    // Sheet 1: Preview Overview
    const overviewData = [
      ['Snijlijsten Preview Export', '', '', '', ''],
      ['Geëxporteerd op:', new Date().toLocaleString('nl-NL'), '', '', ''],
      ['', '', '', '', ''],
      ['Samenvatting', '', '', '', ''],
      ['Totaal producten:', preview.summary?.totalProducts || 0, '', '', ''],
      ['Totaal orders:', preview.summary?.totalOrders || 0, '', '', ''],
      ['Totaal items:', preview.summary?.totalItems || 0, '', '', ''],
      ['Totaal stuks:', preview.summary?.totalQuantity || 0, '', '', ''],
      ['Aantal machines:', preview.summary?.machineCount || 0, '', '', ''],
      ['Niet toegewezen:', preview.summary?.unassignedCount || 0, '', '', ''],
    ];
    const wsOverview = XLSX.utils.aoa_to_sheet(overviewData);
    wsOverview['!cols'] = [{ wch: 20 }, { wch: 25 }, { wch: 15 }, { wch: 15 }, { wch: 15 }];
    XLSX.utils.book_append_sheet(wb, wsOverview, 'Overzicht');

    // Sheet 2: Products with machine assignments
    const productsHeader = [
      'Product Code', 'Product Naam', 'Materiaal Type', 'Rolbreedte (cm)',
      'Toegewezen Machine', 'Reden', 'Totaal Stuks', 'Aantal Orders', 'Vormen'
    ];
    const productsRows = preview.products.map(p => {
      const selectedMachine = getProductMachine(p);
      return [
        p.productCode || '-',
        p.productName || '-',
        p.materialType || '-',
        p.rollWidth || 140,
        selectedMachine?.name || 'Niet toegewezen',
        p.machineReason || '-',
        p.totalQuantity || 0,
        p.orderCount || 0,
        p.shapes?.map(s => s.label).join(', ') || '-'
      ];
    });
    const wsProducts = XLSX.utils.aoa_to_sheet([productsHeader, ...productsRows]);
    wsProducts['!cols'] = [
      { wch: 20 }, { wch: 35 }, { wch: 20 }, { wch: 15 },
      { wch: 25 }, { wch: 40 }, { wch: 12 }, { wch: 15 }, { wch: 30 }
    ];
    XLSX.utils.book_append_sheet(wb, wsProducts, 'Producten');

    // Sheet 3: All Items from preview
    const itemsHeader = [
      'Product Code', 'Product Naam', 'Machine', 'Order Nr', 'Klant',
      'Aantal', 'Breedte (cm)', 'Lengte (cm)', 'Vorm', 'Prioriteit'
    ];
    const itemsRows = [];
    preview.products.forEach(p => {
      const selectedMachine = getProductMachine(p);
      (p.items || []).forEach(item => {
        itemsRows.push([
          p.productCode || '-',
          p.productName || '-',
          selectedMachine?.name || 'Niet toegewezen',
          item.orderNumber || '-',
          item.customer?.name || '-',
          item.quantity || 0,
          item.dimensions?.width || '-',
          item.dimensions?.height || '-',
          item.shapeLabel || item.shape || '-',
          item.priority || 0
        ]);
      });
    });
    const wsItems = XLSX.utils.aoa_to_sheet([itemsHeader, ...itemsRows]);
    wsItems['!cols'] = [
      { wch: 20 }, { wch: 35 }, { wch: 25 }, { wch: 15 }, { wch: 25 },
      { wch: 10 }, { wch: 12 }, { wch: 12 }, { wch: 20 }, { wch: 10 }
    ];
    XLSX.utils.book_append_sheet(wb, wsItems, 'Alle Items');

    // Sheet 4: Machine groups summary
    if (preview.machineGroups?.length) {
      const machineHeader = ['Machine', 'Aantal Producten', 'Totaal Stuks', 'Aantal Orders'];
      const machineRows = preview.machineGroups.map(g => [
        g.machine?.name || 'Niet toegewezen',
        g.productCount || 0,
        g.totalQuantity || 0,
        g.orderCount || 0
      ]);
      const wsMachines = XLSX.utils.aoa_to_sheet([machineHeader, ...machineRows]);
      wsMachines['!cols'] = [{ wch: 25 }, { wch: 18 }, { wch: 15 }, { wch: 15 }];
      XLSX.utils.book_append_sheet(wb, wsMachines, 'Per Machine');
    }

    // Generate filename with date
    const dateStr = new Date().toISOString().split('T')[0];
    const filename = `snijlijsten_preview_${dateStr}.xlsx`;

    // Save file
    XLSX.writeFile(wb, filename);
  };

  // Handle machine change for a product
  const handleProductMachineChange = (productCode, machineId) => {
    setProductMachineOverrides(prev => ({
      ...prev,
      [productCode]: machineId
    }));
  };

  // Get the selected machine for a product (override or assigned)
  const getProductMachine = (productData) => {
    const overrideMachineId = productMachineOverrides[productData.productCode];
    if (overrideMachineId) {
      // First try compatible machines, then all machines
      const fromCompatible = productData.compatibleMachines?.find(m => m._id === overrideMachineId);
      if (fromCompatible) return fromCompatible;
      // Fallback to all machines list
      const fromAll = machines.find(m => m._id === overrideMachineId);
      if (fromAll) return { _id: fromAll._id, name: fromAll.name };
      return null;
    }
    return productData.assignedMachine;
  };

  // Selection functions for cutlists
  const toggleCutListSelection = (cutListId) => {
    const newSelected = new Set(selectedCutLists);
    if (newSelected.has(cutListId)) {
      newSelected.delete(cutListId);
    } else {
      newSelected.add(cutListId);
    }
    setSelectedCutLists(newSelected);
  };

  const selectAllCutLists = () => {
    // Select all visible cutlists
    if (selectedCutLists.size === cutLists.length) {
      setSelectedCutLists(new Set());
    } else {
      setSelectedCutLists(new Set(cutLists.map(cl => cl._id)));
    }
  };

  // Get selected cutlists by status for bulk actions
  const getSelectedByStatus = () => {
    const selected = cutLists.filter(cl => selectedCutLists.has(cl._id));
    return {
      draft: selected.filter(cl => cl.status === 'draft'),
      ready: selected.filter(cl => cl.status === 'ready'),
      in_progress: selected.filter(cl => cl.status === 'in_progress'),
      completed: selected.filter(cl => cl.status === 'completed'),
      deletable: selected.filter(cl => cl.status !== 'in_progress')
    };
  };

  const handleBulkStatusChange = async (newStatus) => {
    for (const cutListId of selectedCutLists) {
      const cutList = cutLists.find(cl => cl._id === cutListId);
      if (cutList) {
        // For in_progress, first mark draft as ready
        if (newStatus === 'in_progress' && cutList.status === 'draft') {
          await dispatch(updateCutListStatus({ id: cutListId, status: 'ready' }));
        }
        // Only update if status change makes sense
        const validTransitions = {
          draft: ['ready', 'in_progress', 'cancelled'],
          ready: ['in_progress', 'draft', 'cancelled'],
          in_progress: ['completed', 'draft'], // Allow completing or going back to draft
          completed: ['draft'], // Allow re-opening
          cancelled: ['draft']
        };
        if (validTransitions[cutList.status]?.includes(newStatus)) {
          await dispatch(updateCutListStatus({ id: cutListId, status: newStatus }));
        }
      }
    }
    setSelectedCutLists(new Set());
    dispatch(fetchCutLists(filters));
  };

  const handleBulkDelete = async () => {
    const deletable = cutLists.filter(cl => selectedCutLists.has(cl._id) && cl.status !== 'in_progress');
    if (deletable.length === 0) {
      alert('Geen snijlijsten om te verwijderen (snijlijsten in productie kunnen niet verwijderd worden).');
      return;
    }
    if (window.confirm(`Weet je zeker dat je ${deletable.length} snijlijst(en) wilt verwijderen?`)) {
      for (const cutList of deletable) {
        await dispatch(deleteCutList(cutList._id));
      }
      setSelectedCutLists(new Set());
      dispatch(fetchCutLists(filters));
    }
  };

  const toggleDeviationExpanded = (orderId) => {
    setExpandedDeviations(prev => ({
      ...prev,
      [orderId]: !prev[orderId]
    }));
  };

  const handleAcceptDeviation = (orderId, itemId) => {
    const key = `${orderId}_${itemId}`;
    setItemOverrides(prev => ({
      ...prev,
      [key]: { accepted: true, machineId: null }
    }));
  };

  const handleOverrideMachine = (orderId, itemId, machineId) => {
    const key = `${orderId}_${itemId}`;
    setItemOverrides(prev => ({
      ...prev,
      [key]: { accepted: false, machineId }
    }));
  };

  const getItemOverride = (orderId, itemId) => {
    const key = `${orderId}_${itemId}`;
    return itemOverrides[key];
  };

  const handleMoveEntireOrder = (orderId, items, machineId) => {
    // Move all items in the order to the specified machine
    const newOverrides = { ...itemOverrides };
    items.forEach(item => {
      const key = `${orderId}_${item.itemId}`;
      newOverrides[key] = { accepted: false, machineId };
    });
    setItemOverrides(newOverrides);
  };

  const handleAcceptEntireOrder = (orderId, items) => {
    // Accept suggested machines for all items in the order
    const newOverrides = { ...itemOverrides };
    items.forEach(item => {
      if (item.isShapeRouted) {
        const key = `${orderId}_${item.itemId}`;
        newOverrides[key] = { accepted: true, machineId: null };
      }
    });
    setItemOverrides(newOverrides);
  };

  // Get machines that can handle all items in an order (support all required shapes)
  const getCompatibleMachinesForOrder = (items) => {
    if (!items || items.length === 0) return [];

    // Collect all unique shapes needed
    const requiredShapes = new Set();
    items.forEach(item => {
      if (item.shape) requiredShapes.add(item.shape);
    });

    // Find machines from the first item's compatible list that support all shapes
    const allCompatible = items[0]?.compatibleMachines || [];
    return allCompatible.filter(machine => {
      const supportedShapes = (machine.supportedShapes || []).map(s => typeof s === 'string' ? s : s?.shape);
      // If no shapes defined, assume it supports all
      if (supportedShapes.length === 0) return true;
      // Check if machine supports all required shapes
      return Array.from(requiredShapes).every(shape => supportedShapes.includes(shape));
    });
  };

  const handleStatusChange = (id, status) => {
    dispatch(updateCutListStatus({ id, status }));
  };

  const handleDelete = (id) => {
    if (window.confirm('Weet je zeker dat je deze snijlijst wilt verwijderen?')) {
      dispatch(deleteCutList(id));
    }
  };

  const handleDeleteAll = () => {
    const draftCount = (cutLists || []).filter(c => ['draft', 'ready', 'cancelled'].includes(c.status)).length;
    if (draftCount === 0) {
      alert('Er zijn geen snijlijsten om te verwijderen (alleen concept/gereed/geannuleerde lijsten worden verwijderd).');
      return;
    }
    if (window.confirm(`Weet je zeker dat je alle ${draftCount} concept/gereed snijlijsten wilt verwijderen?\n\nSnijlijsten die in productie of voltooid zijn blijven behouden.`)) {
      dispatch(deleteAllCutLists({})).then(() => {
        dispatch(fetchCutLists(filters));
      });
    }
  };

  const materials = [
    { value: '', label: 'Alle materialen' },
    { value: 'vinyl_140cm', label: 'Vinyl 140cm' },
    { value: 'vinyl_160cm', label: 'Vinyl 160cm' },
    { value: 'canvas_250cm', label: 'Canvas 250cm' },
    { value: 'pvc', label: 'PVC' }
  ];

  const statuses = [
    { value: 'pending', label: 'Te verwerken (concept/gereed)' },
    { value: '', label: 'Alle statussen' },
    { value: 'draft', label: 'Concept' },
    { value: 'ready', label: 'Gereed' },
    { value: 'in_progress', label: 'In productie' },
    { value: 'completed', label: 'Voltooid' }
  ];

  return (
    <div className="cutlists-page">
      <div className="page-header d-flex justify-content-between align-items-center">
        <div>
          <h1>Snijlijsten</h1>
          <p className="text-muted">Beheer snijlijsten per machine</p>
        </div>
        <div className="d-flex gap-2">
          <button
            className="btn btn-outline-success"
            onClick={handleExportExcel}
            disabled={loading || !cutLists.length}
            title="Exporteer alle snijlijsten naar Excel"
          >
            <i className="bi bi-file-earmark-excel me-2"></i>
            Excel Export
          </button>
          <button
            className="btn btn-outline-danger"
            onClick={handleDeleteAll}
            disabled={loading}
          >
            <i className="bi bi-trash me-2"></i>
            Alles wissen
          </button>
          <button
            className="btn btn-outline-primary"
            onClick={() => {
              setGenerateOptions({ machineIds: [] });
              dispatch(previewCutLists({ machineIds: [] })).then((result) => {
                if (!result.error) {
                  setShowPreviewModal(true);
                }
              });
            }}
            disabled={previewing}
          >
            {previewing ? (
              <span className="spinner-border spinner-border-sm me-2" role="status"></span>
            ) : (
              <i className="bi bi-magic me-2"></i>
            )}
            Auto-genereren
          </button>
          <button
            className="btn btn-primary"
            onClick={() => setShowCreateModal(true)}
          >
            <i className="bi bi-plus-lg me-2"></i>
            Nieuwe snijlijst
          </button>
        </div>
      </div>

      {/* Info Cards */}
      <div className="row mb-4">
        <div className="col-md-3">
          <div className="card bg-light">
            <div className="card-body text-center">
              <h3 className="text-secondary">{(cutLists || []).filter(c => c.status === 'draft').length}</h3>
              <small className="text-muted">Concept</small>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card bg-light">
            <div className="card-body text-center">
              <h3 className="text-primary">{(cutLists || []).filter(c => c.status === 'ready').length}</h3>
              <small className="text-muted">Gereed voor productie</small>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card bg-light">
            <div className="card-body text-center">
              <h3>{(cutLists || []).length}</h3>
              <small className="text-muted">Totaal snijlijsten</small>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card bg-light">
            <div className="card-body text-center">
              <h3>{(cutLists || []).reduce((sum, c) => sum + (c.items?.length || 0), 0)}</h3>
              <small className="text-muted">Totaal items</small>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label">Zoeken</label>
              <div className="input-group">
                <span className="input-group-text"><i className="bi bi-search"></i></span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="ID, naam, materiaal, ordernummer..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      setFilters({ ...filters, search: searchInput, page: 1 });
                    }
                  }}
                />
                {searchInput && (
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => {
                      setSearchInput('');
                      setFilters({ ...filters, search: '', page: 1 });
                    }}
                  >
                    <i className="bi bi-x"></i>
                  </button>
                )}
              </div>
            </div>
            <div className="col-md-3">
              <label className="form-label">Machine</label>
              <select
                className="form-select"
                value={filters.machine}
                onChange={(e) => setFilters({ ...filters, machine: e.target.value, page: 1 })}
              >
                <option value="">Alle machines</option>
                {machines.map((machine) => (
                  <option key={machine._id} value={machine._id}>
                    {machine.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
              >
                {statuses.map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-2 d-flex align-items-end">
              <button
                className="btn btn-secondary w-100"
                onClick={() => {
                  setSearchInput('');
                  setFilters({ machine: '', status: '', search: '', page: 1 });
                }}
              >
                Reset
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Active filter badges */}
      {(filters.machine || (filters.status && filters.status !== 'pending') || filters.search) && (
        <div className="mb-3 d-flex align-items-center gap-2 flex-wrap">
          <small className="text-muted">Actieve filters:</small>
          {filters.machine && (
            <span className="badge bg-primary d-inline-flex align-items-center gap-1">
              <i className="bi bi-gear"></i>
              {machines.find(m => m._id === filters.machine)?.name || 'Machine'}
              <button
                type="button"
                className="btn-close btn-close-white ms-1"
                style={{ fontSize: '0.5rem' }}
                onClick={() => setFilters({ ...filters, machine: '', page: 1 })}
              ></button>
            </span>
          )}
          {filters.status && filters.status !== 'pending' && (
            <span className="badge bg-info d-inline-flex align-items-center gap-1">
              <i className="bi bi-funnel"></i>
              {statuses.find(s => s.value === filters.status)?.label || filters.status}
              <button
                type="button"
                className="btn-close btn-close-white ms-1"
                style={{ fontSize: '0.5rem' }}
                onClick={() => setFilters({ ...filters, status: 'pending', page: 1 })}
              ></button>
            </span>
          )}
          {filters.search && (
            <span className="badge bg-secondary d-inline-flex align-items-center gap-1">
              <i className="bi bi-search"></i>
              "{filters.search}"
              <button
                type="button"
                className="btn-close btn-close-white ms-1"
                style={{ fontSize: '0.5rem' }}
                onClick={() => { setSearchInput(''); setFilters({ ...filters, search: '', page: 1 }); }}
              ></button>
            </span>
          )}
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => { setSearchInput(''); setFilters({ machine: '', status: 'pending', search: '', page: 1 }); }}
          >
            Alles wissen
          </button>
        </div>
      )}

      {/* Cut Lists Table */}
      <div className="card">
        {/* Selection actions bar */}
        {selectedCutLists.size > 0 && (() => {
          const byStatus = getSelectedByStatus();
          return (
          <div className="card-header bg-primary bg-opacity-10">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <span>
                <strong>{selectedCutLists.size}</strong> snijlijst(en) geselecteerd
                <small className="text-muted ms-2">
                  ({byStatus.draft.length} concept, {byStatus.ready.length} gereed, {byStatus.in_progress.length} in productie, {byStatus.completed.length} voltooid)
                </small>
              </span>
              <div className="d-flex gap-2 flex-wrap">
                {/* Mark as Ready - for draft items */}
                {byStatus.draft.length > 0 && (
                  <button
                    className="btn btn-sm btn-info"
                    onClick={() => handleBulkStatusChange('ready')}
                    title={`${byStatus.draft.length} concept → gereed`}
                  >
                    <i className="bi bi-check me-1"></i>
                    Gereed ({byStatus.draft.length})
                  </button>
                )}
                {/* Start Production - for draft/ready items */}
                {(byStatus.draft.length > 0 || byStatus.ready.length > 0) && (
                  <button
                    className="btn btn-sm btn-success"
                    onClick={() => handleBulkStatusChange('in_progress')}
                    title={`${byStatus.draft.length + byStatus.ready.length} items starten`}
                  >
                    <i className="bi bi-play-fill me-1"></i>
                    Start productie ({byStatus.draft.length + byStatus.ready.length})
                  </button>
                )}
                {/* Mark Completed - for in_progress items */}
                {byStatus.in_progress.length > 0 && (
                  <button
                    className="btn btn-sm btn-success"
                    onClick={() => handleBulkStatusChange('completed')}
                    title={`${byStatus.in_progress.length} items voltooien`}
                  >
                    <i className="bi bi-check-lg me-1"></i>
                    Voltooid ({byStatus.in_progress.length})
                  </button>
                )}
                {/* Back to draft - for ready/in_progress/completed items */}
                {(byStatus.ready.length > 0 || byStatus.in_progress.length > 0 || byStatus.completed.length > 0) && (
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => handleBulkStatusChange('draft')}
                    title={`${byStatus.ready.length + byStatus.in_progress.length + byStatus.completed.length} items naar concept`}
                  >
                    <i className="bi bi-arrow-counterclockwise me-1"></i>
                    Concept ({byStatus.ready.length + byStatus.in_progress.length + byStatus.completed.length})
                  </button>
                )}
                {/* Delete - for non-in_progress items */}
                {byStatus.deletable.length > 0 && (
                  <button
                    className="btn btn-sm btn-outline-danger"
                    onClick={handleBulkDelete}
                    title={`${byStatus.deletable.length} items verwijderen`}
                  >
                    <i className="bi bi-trash me-1"></i>
                    Verwijderen ({byStatus.deletable.length})
                  </button>
                )}
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setSelectedCutLists(new Set())}
                >
                  Deselecteer
                </button>
              </div>
            </div>
          </div>
        );
        })()}
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Laden...</span>
              </div>
            </div>
          ) : !cutLists || cutLists.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="bi bi-scissors fs-1 d-block mb-3"></i>
              <p>Geen snijlijsten gevonden</p>
              <button
                className="btn btn-primary"
                disabled={previewing}
                onClick={() => {
                  setGenerateOptions({ machineIds: [] });
                  dispatch(previewCutLists({ machineIds: [] })).then((result) => {
                    if (!result.error) {
                      setShowPreviewModal(true);
                    }
                  });
                }}
              >
                {previewing ? (
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                ) : null}
                Snijlijsten genereren
              </button>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead>
                  <tr>
                    <th style={{ width: '40px' }}>
                      {cutLists.length > 0 && (
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={selectedCutLists.size === cutLists.length && cutLists.length > 0}
                          onChange={selectAllCutLists}
                          title="Selecteer alle"
                        />
                      )}
                    </th>
                    <th>ID</th>
                    <th>Naam</th>
                    <th>Machine</th>
                    <th>Locatie</th>
                    <th>Items</th>
                    <th>Status</th>
                    <th>Aangemaakt</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {cutLists.map((cutList) => {
                    return (
                    <tr key={cutList._id} className={selectedCutLists.has(cutList._id) ? 'table-primary' : ''}>
                      <td>
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={selectedCutLists.has(cutList._id)}
                          onChange={() => toggleCutListSelection(cutList._id)}
                        />
                      </td>
                      <td>
                        <Link to={`/cutlists/${cutList._id}`}>
                          <code>{cutList.cutListId}</code>
                        </Link>
                      </td>
                      <td>
                        <Link to={`/cutlists/${cutList._id}`} className="fw-medium">
                          {cutList.name || '-'}
                        </Link>
                        {cutList.items?.some(item => item.priority >= URGENT_PRIORITY && item.status === 'pending') && (
                          <span className="badge bg-danger ms-2" title="Bevat spoedorders (marketplace)">Spoed</span>
                        )}
                        {cutList.orderStartedItemCount > 0 && (
                          <span className="badge bg-warning text-dark ms-2" title="Bevat regels van orders die al deels in productie zijn: deze lijst gaat voor">
                            Order gestart ({cutList.orderStartedItemCount})
                          </span>
                        )}
                      </td>
                      <td>
                        {cutList.machine ? (
                          <Link to={`/machines/${cutList.machine._id || cutList.machine}`}>
                            {cutList.machine.name || cutList.machine}
                          </Link>
                        ) : '-'}
                      </td>
                      <td className="small">
                        {cutList.stockLocations?.length > 0
                          ? cutList.stockLocations.map(loc => loc.name).join(', ')
                          : <span className="text-muted">-</span>}
                      </td>
                      <td>
                        <span className="badge bg-secondary">
                          {cutList.items?.length || 0} items
                        </span>
                        {cutList.stats && (
                          <small className="d-block text-muted">
                            {cutList.stats.completedItems || 0}/{cutList.stats.totalItems || 0} voltooid
                          </small>
                        )}
                      </td>
                      <td>
                        <StatusBadge status={cutList.status} />
                      </td>
                      <td className="text-muted small">
                        {new Date(cutList.createdAt).toLocaleDateString('nl-NL')}
                      </td>
                      <td>
                        <div className="dropdown" style={{ position: 'relative' }}>
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => setOpenDropdown(openDropdown === cutList._id ? null : cutList._id)}
                          >
                            <i className="bi bi-three-dots-vertical"></i>
                          </button>
                          {openDropdown === cutList._id && (
                            <ul className="dropdown-menu dropdown-menu-end show" style={{ position: 'absolute', right: 0, top: '100%', zIndex: 1000 }}>
                              <li>
                                <Link className="dropdown-item" to={`/cutlists/${cutList._id}`}>
                                  <i className="bi bi-eye me-2"></i>
                                  Bekijken
                                </Link>
                              </li>
                              {(cutList.status === 'draft' || cutList.status === 'ready') && (
                                <li>
                                  <button
                                    className="dropdown-item text-success"
                                    onClick={async () => {
                                      if (cutList.status === 'draft') {
                                        await dispatch(updateCutListStatus({ id: cutList._id, status: 'ready' }));
                                      }
                                      await dispatch(updateCutListStatus({ id: cutList._id, status: 'in_progress' }));
                                      dispatch(fetchCutLists(filters));
                                      setOpenDropdown(null);
                                    }}
                                  >
                                    <i className="bi bi-play-fill me-2"></i>
                                    Start productie
                                  </button>
                                </li>
                              )}
                              {cutList.status === 'in_progress' && (
                                <li>
                                  <button
                                    className="dropdown-item text-success"
                                    onClick={() => {
                                      handleStatusChange(cutList._id, 'completed');
                                      setOpenDropdown(null);
                                    }}
                                  >
                                    <i className="bi bi-check me-2"></i>
                                    Markeer voltooid
                                  </button>
                                </li>
                              )}
                              <li><hr className="dropdown-divider" /></li>
                              <li>
                                <button
                                  className="dropdown-item text-danger"
                                  onClick={() => {
                                    handleDelete(cutList._id);
                                    setOpenDropdown(null);
                                  }}
                                  disabled={cutList.status === 'in_progress'}
                                >
                                  <i className="bi bi-trash me-2"></i>
                                  Verwijderen
                                </button>
                              </li>
                            </ul>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="card-footer d-flex justify-content-between align-items-center">
            <small className="text-muted">
              Pagina {pagination.page} van {pagination.totalPages} ({pagination.total} snijlijsten)
            </small>
            <nav>
              <ul className="pagination pagination-sm mb-0">
                <li className={`page-item ${pagination.page <= 1 ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => setFilters({ ...filters, page: pagination.page - 1 })}
                    disabled={pagination.page <= 1}
                  >
                    &laquo;
                  </button>
                </li>
                {[...Array(pagination.totalPages)].map((_, i) => (
                  <li key={i + 1} className={`page-item ${pagination.page === i + 1 ? 'active' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => setFilters({ ...filters, page: i + 1 })}
                    >
                      {i + 1}
                    </button>
                  </li>
                ))}
                <li className={`page-item ${pagination.page >= pagination.totalPages ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => setFilters({ ...filters, page: pagination.page + 1 })}
                    disabled={pagination.page >= pagination.totalPages}
                  >
                    &raquo;
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Nieuwe Snijlijst</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowCreateModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Naam (optioneel)</label>
                  <input
                    type="text"
                    className="form-control"
                    value={newCutList.name}
                    onChange={(e) => setNewCutList({ ...newCutList, name: e.target.value })}
                    placeholder="Bijv. Ochtend productie"
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Machine *</label>
                  <select
                    className="form-select"
                    value={newCutList.machineId}
                    onChange={(e) => setNewCutList({ ...newCutList, machineId: e.target.value })}
                    required
                  >
                    <option value="">Selecteer machine</option>
                    {machines.filter(m => m.isActive).map((machine) => (
                      <option key={machine._id} value={machine._id}>
                        {machine.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label">Materiaal</label>
                  <select
                    className="form-select"
                    value={newCutList.material}
                    onChange={(e) => setNewCutList({ ...newCutList, material: e.target.value })}
                  >
                    <option value="">Automatisch (op basis van orders)</option>
                    {materials.filter(m => m.value).map((mat) => (
                      <option key={mat.value} value={mat.value}>
                        {mat.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowCreateModal(false)}
                >
                  Annuleren
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleCreate}
                  disabled={!newCutList.machineId}
                >
                  Aanmaken
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal - Grouped by Product */}
      {showPreviewModal && preview && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-xl modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-clipboard-data me-2"></i>
                  Analyse Snijlijsten
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleClosePreview}
                ></button>
              </div>
              <div className="modal-body">
                {/* Summary Cards */}
                <div className="row mb-4">
                  <div className="col-md-2">
                    <div className="card bg-light">
                      <div className="card-body text-center py-2">
                        <h4 className="mb-0 text-primary">{preview.summary?.totalProducts || 0}</h4>
                        <small className="text-muted">Producten</small>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div className="card bg-light">
                      <div className="card-body text-center py-2">
                        <h4 className="mb-0">{preview.summary?.totalOrders || 0}</h4>
                        <small className="text-muted">Orders</small>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div className="card bg-light">
                      <div className="card-body text-center py-2">
                        <h4 className="mb-0">{preview.summary?.totalItems || 0}</h4>
                        <small className="text-muted">Items</small>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div className="card bg-success bg-opacity-25">
                      <div className="card-body text-center py-2">
                        <h4 className="mb-0 text-success">{preview.summary?.totalQuantity || 0}</h4>
                        <small className="text-muted">Stuks</small>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div className="card bg-info bg-opacity-25">
                      <div className="card-body text-center py-2">
                        <h4 className="mb-0 text-info">{preview.summary?.machineCount || 0}</h4>
                        <small className="text-muted">Machines</small>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-2">
                    {preview.summary?.unassignedCount > 0 ? (
                      <div className="card bg-warning bg-opacity-25">
                        <div className="card-body text-center py-2">
                          <h4 className="mb-0 text-warning">{preview.summary.unassignedCount}</h4>
                          <small className="text-muted">Niet toegewezen</small>
                        </div>
                      </div>
                    ) : (
                      <div className="card bg-success bg-opacity-25">
                        <div className="card-body text-center py-2">
                          <h4 className="mb-0 text-success"><i className="bi bi-check-lg"></i></h4>
                          <small className="text-muted">Alles toegewezen</small>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Machine Groups Summary */}
                {preview.machineGroups && preview.machineGroups.length > 0 && (
                  <div className="mb-4">
                    <h6 className="mb-3">
                      <i className="bi bi-pc-display me-2"></i>
                      Verdeling per machine
                    </h6>
                    <div className="row">
                      {preview.machineGroups.map((group) => (
                        <div key={group.machine._id} className="col-md-4 mb-2">
                          <div className="card h-100">
                            <div className="card-body py-2">
                              <div className="d-flex justify-content-between align-items-center">
                                <h6 className="card-title mb-0">{group.machine.name}</h6>
                                <span className="badge bg-primary">{group.totalQuantity} stuks</span>
                              </div>
                              <small className="text-muted">
                                {group.productCount} producten • {group.orderCount} orders
                              </small>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Products Section */}
                {preview.products && preview.products.length > 0 ? (
                  <div className="mb-4">
                    <h6 className="mb-3">
                      <i className="bi bi-box-seam me-2"></i>
                      Producten ({preview.products.length})
                    </h6>
                    <p className="text-muted small mb-3">
                      Klik op een product om de orders te zien die dit product bevatten.
                    </p>

                    {[...preview.products].sort((a, b) => (a.materialType || '').localeCompare(b.materialType || '')).map((productData) => {
                      const selectedMachine = getProductMachine(productData);
                      const hasOverride = productMachineOverrides[productData.productCode];

                      return (
                      <div key={productData.productCode} className={`card mb-2 ${!selectedMachine ? 'border-warning' : ''} ${hasOverride ? 'border-primary' : ''}`}>
                        <div
                          className="card-header py-2"
                          style={{ cursor: 'pointer' }}
                          onClick={() => toggleDeviationExpanded(productData.productCode)}
                        >
                          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <div className="d-flex align-items-center flex-wrap gap-2">
                              <strong>{productData.productCode}</strong>
                              <span className="text-muted">-</span>
                              <span className="text-truncate" style={{ maxWidth: '300px' }}>{productData.productName}</span>
                              {productData.materialType && (
                                <span className="badge bg-info bg-opacity-75 text-dark">{productData.materialType}</span>
                              )}
                            </div>
                            <div className="d-flex align-items-center gap-2">
                              <span className="badge bg-primary">
                                {productData.totalQuantity} stuks
                              </span>
                              <span className="badge bg-secondary">
                                {productData.orderCount} order(s)
                              </span>
                              {productData.shapes?.map(shape => (
                                <span key={shape.value} className="badge bg-light text-dark">
                                  {shape.label}
                                </span>
                              ))}
                              {selectedMachine ? (
                                <span className={`badge ${hasOverride ? 'bg-primary' : 'bg-success'}`}>
                                  <i className={`bi ${hasOverride ? 'bi-pencil' : 'bi-arrow-right'} me-1`}></i>
                                  {selectedMachine.name}
                                </span>
                              ) : (
                                <span className="badge bg-warning text-dark">
                                  <i className="bi bi-exclamation-triangle me-1"></i>
                                  Geen machine
                                </span>
                              )}
                              <i className={`bi bi-chevron-${expandedDeviations[productData.productCode] ? 'up' : 'down'}`}></i>
                            </div>
                          </div>
                        </div>

                        {expandedDeviations[productData.productCode] && (
                          <div className="card-body">
                            {/* Machine assignment info */}
                            <div className={`mb-3 p-2 rounded ${selectedMachine ? 'bg-light' : 'bg-warning bg-opacity-25'}`}>
                              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                <div>
                                  <strong>Machine:</strong>{' '}
                                  {selectedMachine ? (
                                    <span className={hasOverride ? 'text-primary fw-bold' : 'text-success'}>
                                      {selectedMachine.name}
                                      {hasOverride && <i className="bi bi-pencil ms-1"></i>}
                                    </span>
                                  ) : (
                                    <span className="text-warning fw-bold">Niet toegewezen - selecteer een machine</span>
                                  )}
                                  <br />
                                  <small className="text-muted">
                                    <i className="bi bi-info-circle me-1"></i>
                                    {hasOverride ? 'Handmatig aangepast' : productData.machineReason}
                                  </small>
                                </div>
                                <div onClick={(e) => e.stopPropagation()}>
                                  <small className="text-muted me-2">
                                    {selectedMachine ? 'Wijzig machine:' : 'Selecteer machine:'}
                                  </small>
                                  <select
                                    className={`form-select form-select-sm d-inline-block ${
                                      !selectedMachine ? 'border-warning' :
                                      hasOverride ? 'border-primary bg-primary bg-opacity-10' : ''
                                    }`}
                                    style={{ width: 'auto', minWidth: '180px' }}
                                    value={productMachineOverrides[productData.productCode] || productData.assignedMachine?._id || ''}
                                    onChange={(e) => handleProductMachineChange(productData.productCode, e.target.value)}
                                  >
                                    {!selectedMachine && <option value="">-- Selecteer machine --</option>}
                                    {machines.map(m => (
                                      <option key={m._id} value={m._id}>
                                        {m.name} {m.isDefault ? '(standaard)' : ''}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              </div>
                            </div>

                            <div className="table-responsive">
                              <table className="table table-sm mb-0">
                                <thead>
                                  <tr>
                                    <th>Order</th>
                                    <th>Klant</th>
                                    <th>Aantal</th>
                                    <th>Afmetingen</th>
                                    <th>Shape</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {productData.items.map((item, idx) => (
                                    <tr key={idx}>
                                      <td>
                                        <Link to={`/orders/${item.orderId}`} className="text-decoration-none">
                                          #{item.orderNumber}
                                        </Link>
                                      </td>
                                      <td>
                                        {item.customer?.name || 'Onbekend'}
                                      </td>
                                      <td>
                                        <strong>{item.quantity}</strong>
                                      </td>
                                      <td>
                                        {item.dimensions?.width || item.dimensions?.height
                                          ? `${item.dimensions.width || '?'} × ${item.dimensions.height || '?'} ${item.dimensions?.unit || 'mm'}`
                                          : '-'
                                        }
                                      </td>
                                      <td>
                                        <span className="badge bg-secondary">
                                          {item.shapeLabel}
                                        </span>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>

                            {/* Product parts info */}
                            {productData.product?.parts && productData.product.parts.length > 0 && (
                              <div className="mt-3">
                                <small className="text-muted">
                                  <i className="bi bi-diagram-3 me-1"></i>
                                  Dit product heeft {productData.product.parts.length} onderdelen (parts)
                                </small>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                    })}
                  </div>
                ) : (
                  <div className="alert alert-info">
                    <i className="bi bi-info-circle me-2"></i>
                    <strong>Geen producten gevonden</strong>
                    <p className="mb-0 small">
                      Er zijn geen orders met producten die een materiaal type hebben ingesteld.
                    </p>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={handleClosePreview}
                >
                  Annuleren
                </button>
                <button
                  type="button"
                  className="btn btn-outline-success"
                  onClick={handleExportPreviewExcel}
                  disabled={!preview.products?.length}
                  title="Exporteer preview naar Excel"
                >
                  <i className="bi bi-file-earmark-excel me-2"></i>
                  Excel Export
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={handleGenerate}
                  disabled={generating || !preview.products?.length}
                >
                  {generating ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Genereren...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check-lg me-2"></i>
                      Genereren ({preview.summary?.totalQuantity || 0} stuks)
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CutLists;
